import { useMemo, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { FinanceHospitalComparisonRow } from "@/api/intelligence";
import { formatInr } from "@/lib/intelligenceFormat";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = { rows: FinanceHospitalComparisonRow[] };
type SortKey = keyof FinanceHospitalComparisonRow;

export function FinanceHospitalComparisonTable({ rows }: Props) {
	const [sortKey, setSortKey] = useState<SortKey>("netRevenue");
	const [sortAsc, setSortAsc] = useState(false);

	const sorted = useMemo(() => {
		const copy = [...rows];
		copy.sort((a, b) => {
			const av = a[sortKey];
			const bv = b[sortKey];
			if (typeof av === "string" && typeof bv === "string") {
				return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
			}
			return sortAsc ? Number(av) - Number(bv) : Number(bv) - Number(av);
		});
		return copy;
	}, [rows, sortKey, sortAsc]);

	if (!rows.length) return null;

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortAsc(!sortAsc);
		else {
			setSortKey(key);
			setSortAsc(false);
		}
	};

	const SortHead = ({ label, field }: { label: string; field: SortKey }) => (
		<TableHead
			className={cn(
				"cursor-pointer select-none whitespace-nowrap",
				field !== "hospitalName" && "text-right"
			)}
			onClick={() => toggleSort(field)}
		>
			<span className="inline-flex items-center gap-1">
				{label}
				<ArrowUpDown className="h-3 w-3 opacity-50" />
			</span>
		</TableHead>
	);

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">Hospital comparison</CardTitle>
			</CardHeader>
			<CardContent className="overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<SortHead label="Hospital" field="hospitalName" />
							<SortHead label="Net revenue" field="netRevenue" />
							<SortHead label="Collections" field="collections" />
							<SortHead label="Outstanding" field="outstanding" />
							<SortHead label="Collection eff." field="collectionEfficiency" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.map((row) => (
							<TableRow key={row.hospitalId}>
								<TableCell className="font-medium">{row.hospitalName}</TableCell>
								<TableCell className="text-right tabular-nums">{formatInr(row.netRevenue)}</TableCell>
								<TableCell className="text-right tabular-nums">{formatInr(row.collections)}</TableCell>
								<TableCell className="text-right tabular-nums">{formatInr(row.outstanding)}</TableCell>
								<TableCell className="text-right tabular-nums">{row.collectionEfficiency}%</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}
